/**
 * <purchase-matrix>
 *
 * Owns the entire buy-box decision:
 *   - quantity card selection (radiogroup)
 *   - purchase type selection (subscription / one-time radiogroup)
 *   - cadence selection inside Auto Replenish card (radiogroup or select)
 *
 * On any change, recomputes the active (cadence, quantity, purchase-type) cell and:
 *   - updates SAVE % overlays on each quantity card (hides them when One-Time selected)
 *   - updates the Auto Replenish card compare/sale prices
 *   - updates the Auto Replenish "Save X%" benefit line, or during the public
 *     sale plan (cadence.firstOrderFull, first order at full price) the
 *     refill line from Theme settings → Subscribe & Save wording
 *   - updates the One-Time card price
 *   - updates ATC button label, compare-at, sale price
 *   - updates hidden form inputs (`quantity`, `selling_plan`)
 *
 * Fires a `purchasematrix:change` CustomEvent the sticky CTA bar can subscribe to.
 */

(() => {
  if (customElements.get('purchase-matrix')) return;

  // "Save X%" labels: floored, so a compounded 19.25% reads 19% and the page
  // never claims more than the cart takes off. The epsilon stops float noise
  // (18.999999999 for a true 19) from flooring a whole number down.
  const pctLabel = (pct) => Math.floor((Number(pct) || 0) + 1e-9);

  const formatMoney = (cents, format) => {
    const dollars = (cents / 100).toFixed(2);
    if (!format) return `$${dollars}`;
    return format.replace(/\{\{\s*amount\s*\}\}/g, dollars)
                 .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
                 .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','));
  };

  class PurchaseMatrix extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-role="matrix-data"]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[purchase-matrix] failed to parse data:', e);
        return;
      }

      // Products without the bulk feature ship a synthesized single tier
      // from Liquid; this guard keeps the price math alive even if that
      // JSON ever arrives empty (qty 1, no discounts).
      if (!Array.isArray(this.data.quantities) || this.data.quantities.length === 0) {
        this.data.quantities = [{
          qty: this.data.defaultQuantity || 1,
          label: '',
          badge: null,
          saveMonthly: 0,
          saveBimonthly: 0,
          saveOnetime: 0,
          onetimePriceOverride: null,
        }];
      }

      this.state = {
        qty: this.data.defaultQuantity,
        cadenceKey: this.data.defaultCadenceKey,
        purchaseType: this.data.defaultPurchaseType || 'subscription',
        selectedOptions: Array.isArray(this.data.selectedOptions) ? this.data.selectedOptions.slice() : null,
      };

      // Variant awareness (QA launch fixes): resolve the current variant from
      // the server-selected id so price + availability follow the selection.
      this.variants = Array.isArray(this.data.variants) ? this.data.variants : [];
      this.currentVariant = this.variants.find(v => String(v.id) === String(this.data.variantId)) || this.variants[0] || null;
      if (this.currentVariant && typeof this.currentVariant.price === 'number') {
        this.data.basePrice = this.currentVariant.price;
      }

      this.qtyButtons = Array.from(this.querySelectorAll('.salt-qty-card'));
      this.purchaseTypeCards = Array.from(this.querySelectorAll('.salt-purchase-card'));
      this.cadenceButtons = Array.from(this.querySelectorAll('.salt-cadence-toggle__btn'));
      this.cadenceSelect = this.querySelector('[data-role="cadence-select"]');

      this.qtyInput = this.querySelector('[data-role="qty-input"]');
      this.sellingPlanInput = this.querySelector('[data-role="selling-plan-input"]');

      this.subCard = this.querySelector('.salt-purchase-card--subscription');
      this.onetimeCard = this.querySelector('.salt-purchase-card--onetime');

      this.atcButton = this.querySelector('[data-role="atc-button"]');
      this.atcCompare = this.querySelector('[data-role="atc-compare"]');
      this.atcSale = this.querySelector('[data-role="atc-sale"]');
      this.atcLabel = this.querySelector('[data-role="atc-label"]');
      this.optionButtons = Array.from(this.querySelectorAll('[data-role="variant-opt"]'));
      this.variantSelect = this.querySelector('select[name="id"]');

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      this.qtyButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectQty(Number(btn.dataset.qty)));
        btn.addEventListener('keydown', (e) => this.handleRadioKeydown(e, this.qtyButtons, this.selectQty.bind(this), 'qty'));
      });

      this.purchaseTypeCards.forEach(card => {
        card.addEventListener('click', () => this.selectPurchaseType(card.dataset.type));
        card.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            this.selectPurchaseType(card.dataset.type);
          }
        });
      });

      this.cadenceButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.selectCadence(btn.dataset.cadenceKey);
        });
        btn.addEventListener('keydown', (e) => this.handleRadioKeydown(e, this.cadenceButtons, (val) => this.selectCadence(val), 'cadence'));
      });

      if (this.cadenceSelect) {
        this.cadenceSelect.addEventListener('change', () => {
          const opt = this.cadenceSelect.options[this.cadenceSelect.selectedIndex];
          this.selectCadence(opt.value);
        });
      }

      this.optionButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.selectOption(Number(btn.dataset.optionIndex), btn.dataset.value);
        });
      });
    }

    handleRadioKeydown(event, items, selectFn, kind) {
      const { key } = event;
      const navKeys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        const target = event.currentTarget;
        if (kind === 'qty') selectFn(Number(target.dataset.qty));
        else if (kind === 'cadence') selectFn(target.dataset.cadenceKey);
        return;
      }
      if (!navKeys.includes(key)) return;
      event.preventDefault();
      const currentIndex = items.findIndex(el => el.getAttribute('aria-checked') === 'true');
      let nextIndex = currentIndex;
      if (key === 'ArrowRight' || key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
      else if (key === 'ArrowLeft' || key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = items.length - 1;
      items[nextIndex].focus();
      if (kind === 'qty') selectFn(Number(items[nextIndex].dataset.qty));
      else if (kind === 'cadence') selectFn(items[nextIndex].dataset.cadenceKey);
    }

    selectQty(qty) {
      if (qty === this.state.qty) return;
      this.state.qty = qty;
      this.update();
    }

    selectCadence(key) {
      if (key === this.state.cadenceKey) return;
      this.state.cadenceKey = key;
      // Selecting a cadence also activates subscription purchase-type (clicking inside the sub card)
      this.state.purchaseType = 'subscription';
      this.update();
    }

    selectPurchaseType(type) {
      if (type === this.state.purchaseType) return;
      this.state.purchaseType = type;
      this.update();
    }

    /**
     * Option pill clicked: update the selected value for that option
     * position, resolve the matching variant, sync the hidden Blockshop
     * select (so its stock bookkeeping follows), reprice, and re-gate the
     * ATC. Unavailable combinations stay selectable — the QA requirement is
     * that they LOOK unavailable and cannot be purchased, not that they
     * vanish.
     */
    selectOption(optionIndex, value) {
      if (!this.state.selectedOptions) return;
      if (this.state.selectedOptions[optionIndex] === value) return;
      this.state.selectedOptions[optionIndex] = value;

      const match = this.variants.find(v =>
        v.options.every((opt, i) => opt === this.state.selectedOptions[i])
      );
      if (match) {
        this.currentVariant = match;
        if (typeof match.price === 'number') this.data.basePrice = match.price;
        if (this.variantSelect) {
          this.variantSelect.value = String(match.id);
          Array.from(this.variantSelect.options).forEach(o => {
            if (o.value === String(match.id)) o.setAttribute('selected', '');
            else o.removeAttribute('selected');
          });
          this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // Combination doesn't exist as a variant — treat as unavailable.
        this.currentVariant = null;
      }

      this.refreshOptionStates();
      this.update();
    }

    isVariantPurchasable() {
      if (this.variants.length === 0) return this.data.variantAvailable !== false;
      return !!(this.currentVariant && this.currentVariant.available);
    }

    /** Selected + contextual availability state on every pill. A value is
     *  available-in-context when some variant carrying it AND the currently
     *  selected values of every OTHER option is available. */
    refreshOptionStates() {
      this.optionButtons.forEach(btn => {
        const idx = Number(btn.dataset.optionIndex);
        const val = btn.dataset.value;
        const isSelected = this.state.selectedOptions[idx] === val;
        btn.classList.toggle('salt-variant-opt--selected', isSelected);
        btn.setAttribute('aria-checked', String(isSelected));
        btn.tabIndex = isSelected ? 0 : -1;

        const availableInContext = this.variants.some(v =>
          v.available && v.options[idx] === val &&
          v.options.every((opt, i) => i === idx || opt === this.state.selectedOptions[i])
        );
        btn.classList.toggle('salt-variant-opt--unavailable', !availableInContext);
      });
    }

    // Derived values
    getCurrentQtyTier() {
      return this.data.quantities.find(q => q.qty === this.state.qty);
    }

    getCurrentCadence() {
      return this.data.cadences.find(c => c.key === this.state.cadenceKey) || this.data.cadences[0];
    }

    /**
     * Selling plan id for a cadence at a given quantity.
     *
     * Shopify plans carry no quantity condition, so a tiered subscription is
     * several plans sharing one cadence ("Every month", "Every month - 2
     * Bottles", …). Liquid groups them into `cadence.plans` keyed by quantity;
     * this picks the right one, falling back to the 1-bottle plan when a tier
     * has not been created yet. That fallback is what keeps the page working
     * while the plans are still being set up — the shopper just gets the
     * untiered subscription rate rather than a broken form.
     */
    planIdFor(cadence, qty) {
      if (!cadence) return '';
      const plans = cadence.plans || {};
      const tier = plans[String(qty)] || plans['1'];
      return (tier && tier.id) || cadence.sellingPlanId || '';
    }

    getCurrentSavePercent() {
      const tier = this.getCurrentQtyTier();
      if (!tier) return 0;
      if (this.state.purchaseType === 'onetime') return tier.saveOnetime || 0;
      return this.subSaveForQty(this.state.qty);
    }

    /**
     * Subscription saving (% of full price) for a quantity at the current
     * cadence.
     *
     * Two pricing models share this page. By default each quantity card
     * carries its own total % per cadence (quantity-tiered selling plans).
     * When the template sets a Skio volume discount for a bottle count, Skio
     * takes it off the already discounted subscription order, so the total
     * is read back from the order price the cart will charge: a dollar
     * amount (`subOff`, e.g. 2.16 off 3 bottles) or a % (`subExtra`, where
     * 15% then a further 5% is 19.25% off, not 20%).
     */
    subSaveForQty(qty) {
      const tier = this.data.quantities.find(q => q.qty === qty);
      const cadence = this.getCurrentCadence();
      const planPct = cadence ? cadence.savePercent : 0;
      if (tier && (tier.subOff != null || tier.subExtra != null)) {
        const full = this.data.basePrice * qty;
        return 100 * (full - this.getSubscriptionPrice(qty)) / full;
      }
      if (!tier) return planPct;
      if (this.state.cadenceKey === 'monthly') return tier.saveMonthly || 0;
      if (this.state.cadenceKey === 'bimonthly') return tier.saveBimonthly || 0;
      // unknown cadence → use plan-level discount as fallback
      return planPct;
    }

    /**
     * Subscription price in cents for a quantity, rounded the way the cart
     * gets there. With a Skio volume discount: the selling plan prices one
     * bottle (rounded to the cent), the order is that times the quantity,
     * and the volume discount comes off the order. Without one, the card's
     * total % applies to the full price, as before.
     */
    getSubscriptionPrice(qty) {
      const tier = this.data.quantities.find(q => q.qty === qty);
      if (tier && (tier.subOff != null || tier.subExtra != null)) {
        const cadence = this.getCurrentCadence();
        const planPct = cadence ? cadence.savePercent : 0;
        const unit = Math.round(this.data.basePrice * (100 - planPct) / 100);
        if (tier.subOff != null) return unit * qty - tier.subOff;
        return Math.round(unit * qty * (100 - tier.subExtra) / 100);
      }
      return Math.round(this.data.basePrice * qty * (100 - this.subSaveForQty(qty)) / 100);
    }

    getComparePrice() {
      return this.data.basePrice * this.state.qty;
    }

    /**
     * One-time price for the current quantity tier, in cents.
     *
     * The per-tier `onetime_price_override` field on the Quantity Card
     * metaobject wins over the integer `save_percent_onetime` calculation:
     * the whole point of the override is that a whole-number % can't land on
     * the merchandiser's exact target (2 Bottles is $34.99 off a $35.98
     * compare — 2.75% off, not an integer).
     *
     * Single source of truth for BOTH the One-Time card and the ATC button.
     * They used to compute this independently — only the card consulted the
     * override — so a tier with an override rendered two different prices on
     * the same screen ($34.99 on the card, $34.90 on the button).
     *
     * The override is a flat cents figure per tier, so it is variant-agnostic
     * by design; the products carrying quantity cards are single-variant.
     */
    getOnetimePrice() {
      const tier = this.getCurrentQtyTier();
      const override = tier && tier.onetimePriceOverride != null
        ? tier.onetimePriceOverride
        : null;
      if (override !== null && override > 0) return override;
      const savePct = tier ? (tier.saveOnetime || 0) : 0;
      return Math.round(this.getComparePrice() * (100 - savePct) / 100);
    }

    getSalePrice() {
      if (this.state.purchaseType === 'onetime') return this.getOnetimePrice();
      return this.getSubscriptionPrice(this.state.qty);
    }

    update() {
      const tier = this.getCurrentQtyTier();
      const cadence = this.getCurrentCadence();
      const savePct = this.getCurrentSavePercent();
      const compare = this.getComparePrice();
      const sale = this.getSalePrice();
      const format = this.data.moneyFormat;

      // --- Quantity card selection state + SAVE overlays ---
      this.qtyButtons.forEach(btn => {
        const btnQty = Number(btn.dataset.qty);
        const isSelected = btnQty === this.state.qty;
        btn.setAttribute('aria-checked', String(isSelected));
        btn.classList.toggle('salt-qty-card--selected', isSelected);

        const overlay = btn.querySelector('[data-role="save-overlay"]');
        if (overlay) {
          if (this.state.purchaseType === 'onetime' || (cadence && cadence.firstOrderFull)) {
            // Public sale plan: the first order is at full price, so a
            // per-quantity "Save X%" would be wrong. The refill line says it.
            overlay.hidden = true;
          } else {
            overlay.hidden = false;
            overlay.textContent = `Save ${pctLabel(this.subSaveForQty(btnQty))}%`;
          }
        }
      });

      // --- Purchase-type card selection state ---
      this.purchaseTypeCards.forEach(card => {
        const isSelected = card.dataset.type === this.state.purchaseType;
        card.setAttribute('aria-checked', String(isSelected));
        card.classList.toggle('salt-purchase-card--selected', isSelected);
      });

      // --- Subscription card price + cadence save line ---
      if (this.subCard) {
        const subSave = pctLabel(this.subSaveFor(tier, cadence));
        const subCompare = compare;
        const subSale = this.getSubscriptionPrice(this.state.qty);
        const subCompareEl = this.subCard.querySelector('[data-role="compare"]');
        const subSaleEl = this.subCard.querySelector('[data-role="sale"]');
        const subSaveLine = this.subCard.querySelector('[data-role="cadence-save"]');
        if (subCompareEl) {
          if (subCompare > subSale) {
            subCompareEl.hidden = false;
            subCompareEl.textContent = formatMoney(subCompare, format);
          } else {
            subCompareEl.hidden = true;
          }
        }
        if (subSaleEl) subSaleEl.textContent = formatMoney(subSale, format);
        if (subSaveLine) subSaveLine.textContent = String(subSave);
        // Public sale plan (first order at full price, refills discounted):
        // swap "Save X%" for the refill line, so the page never says Save 0%.
        const saleShape = Boolean(cadence && cadence.firstOrderFull);
        const normalLine = this.subCard.querySelector('[data-role="save-normal"]');
        const saleLine = this.subCard.querySelector('[data-role="save-sale"]');
        const refillEl = this.subCard.querySelector('[data-role="cadence-refill"]');
        if (normalLine && saleLine) {
          normalLine.hidden = saleShape;
          saleLine.hidden = !saleShape;
        }
        if (refillEl && cadence) refillEl.textContent = String(pctLabel(cadence.refillPercent || 0));
      }

      // --- Cadence toggle selection state ---
      this.cadenceButtons.forEach(btn => {
        const isSelected = btn.dataset.cadenceKey === this.state.cadenceKey;
        btn.setAttribute('aria-checked', String(isSelected));
        btn.classList.toggle('salt-cadence-toggle__btn--selected', isSelected);
      });

      // --- One-Time card price ---
      if (this.onetimeCard) {
        // Shared with the ATC button via getOnetimePrice() — see the note
        // there on why the per-tier override wins over the integer %.
        const oneCompare = compare;
        const oneSale = this.getOnetimePrice();
        const oneCompareEl = this.onetimeCard.querySelector('[data-role="onetime-compare"]');
        const oneSaleEl = this.onetimeCard.querySelector('[data-role="onetime-sale"]');
        if (oneCompareEl) {
          if (oneCompare > oneSale) {
            oneCompareEl.hidden = false;
            oneCompareEl.textContent = formatMoney(oneCompare, format);
          } else {
            oneCompareEl.hidden = true;
          }
        }
        if (oneSaleEl) oneSaleEl.textContent = formatMoney(oneSale, format);
      }

      // --- ATC button ---
      if (this.atcCompare) {
        if (compare > sale) {
          this.atcCompare.hidden = false;
          this.atcCompare.textContent = formatMoney(compare, format);
        } else {
          this.atcCompare.hidden = true;
        }
      }
      if (this.atcSale) this.atcSale.textContent = formatMoney(sale, format);

      // --- Sold-out gate (QA launch fix) ---
      const purchasable = this.isVariantPurchasable();
      if (this.atcButton) {
        this.atcButton.disabled = !purchasable;
        this.atcButton.dataset.enabled = String(purchasable);
        if (this.atcLabel) {
          this.atcLabel.textContent = purchasable
            ? (this.data.atcLabel || this.atcButton.dataset.text || 'ADD TO CART')
            : (this.data.soldOutLabel || this.atcButton.dataset.soldoutText || 'SOLD OUT');
        }
        if (!purchasable) {
          if (this.atcCompare) this.atcCompare.hidden = true;
          if (this.atcSale) this.atcSale.hidden = true;
        } else if (this.atcSale) {
          this.atcSale.hidden = false;
        }
      }

      // --- Hidden form inputs ---
      if (this.qtyInput) this.qtyInput.value = String(this.state.qty);
      if (this.sellingPlanInput) {
        if (this.state.purchaseType === 'onetime') {
          this.sellingPlanInput.value = '';
        } else {
          this.sellingPlanInput.value = this.planIdFor(cadence, this.state.qty);
        }
      }

      // --- Fire change event for sticky CTA + other listeners ---
      const changeDetail = {
        variantId: this.currentVariant ? this.currentVariant.id : this.data.variantId,
        available: this.isVariantPurchasable(),
        qty: this.state.qty,
        cadenceKey: this.state.cadenceKey,
        purchaseType: this.state.purchaseType,
        savePercent: savePct,
        compare,
        sale,
        formattedCompare: formatMoney(compare, format),
        formattedSale: formatMoney(sale, format),
        sellingPlanId: this.state.purchaseType === 'subscription' ? this.planIdFor(cadence, this.state.qty) : '',
      };
      // Cache the latest detail so listeners that attach AFTER the initial
      // dispatch (fired during connectedCallback) — e.g. the Afterpay amount
      // sync — can read the current state instead of waiting for the next
      // user interaction.
      this.lastChange = changeDetail;
      this.dispatchEvent(new CustomEvent('purchasematrix:change', {
        bubbles: true,
        detail: changeDetail,
      }));
    }

    /**
     * Save % to show on the subscription card's "✓ Save X%" benefit line, based on
     * the currently-selected (qty, cadence). Even when one-time is the active
     * purchase type, the sub card still displays its own savings.
     */
    subSaveFor(tier, cadence) {
      if (!tier) return cadence ? cadence.savePercent : 0;
      return this.subSaveForQty(tier.qty);
    }
  }

  customElements.define('purchase-matrix', PurchaseMatrix);
})();
